import * as React from "react";
import { toast } from "sonner";
import { ArrowUpRight, Clock, Mail, MapPin, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useInView } from "@/hooks/useInView";

const CONTACT_DETAILS = [
  { icon: Mail, label: "info@vision-one.io", caption: "Antwort meist am selben Tag" },
  { icon: Phone, label: "+49 1556 7304351", caption: "Mo–Fr, 9–18 Uhr" },
  { icon: MapPin, label: "Augsburg & remote", caption: "Anschrift im Impressum" },
  { icon: Clock, label: "Erstgespräch in 24 h", caption: "unverbindlich & kostenlos" },
];

// Formspark-Endpunkt: Anfragen landen direkt im Postfach
const FORM_ENDPOINT = "https://submit-form.com/PZ3UkWev5";

const EMPTY_FORM = {
  name: "",
  company: "",
  email: "",
  phone: "",
  plan: "",
  message: "",
  privacy: false,
};

export const ContactSection = () => {
  const [ref, inView] = useInView({ threshold: 0.1 });
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [sending, setSending] = React.useState(false);

  const update = (key) => (event) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.name || !form.email || !form.privacy) {
      toast.error("Bitte Name, E-Mail und Datenschutz bestätigen.");
      return;
    }

    setSending(true);

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          Name: form.name,
          Unternehmen: form.company,
          "E-Mail": form.email,
          Telefon: form.phone,
          Paket: form.plan,
          Nachricht: form.message,
          Datenschutz: form.privacy ? "akzeptiert" : "offen",
          Quelle: "Vision One Website",
        }),
      });

      if (!response.ok) throw new Error("Request failed");

      setForm(EMPTY_FORM);
      toast.success("Danke! Wir melden uns innerhalb von 24 Stunden.", {
        description: "Deine Anfrage ist bei uns eingegangen.",
      });
    } catch (error) {
      toast.error("Das hat leider nicht funktioniert.", {
        description: "Bitte versuche es erneut oder schreib uns direkt per E-Mail.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="kontakt"
      ref={ref}
      className="relative isolate overflow-hidden bg-light text-light-foreground"
      style={{ clipPath: "polygon(0 0, 100% 2.6vw, 100% 100%, 0 100%)" }}
    >
      <div aria-hidden className="absolute inset-0 -z-10 bg-gradient-light" />
      <div
        aria-hidden
        className="absolute -left-24 top-[8%] -z-10 h-[420px] w-[560px] -rotate-6 rounded-[45%] bg-[radial-gradient(closest-side,hsl(214_80%_72%/0.26),transparent)] blur-2xl"
      />
      <div
        aria-hidden
        className="absolute -right-20 bottom-[6%] -z-10 h-[420px] w-[500px] rounded-full bg-[radial-gradient(closest-side,hsl(265_70%_74%/0.2),transparent)] blur-2xl"
      />

      <div className="mx-auto max-w-[1500px] px-5 pb-28 pt-32 sm:px-8 sm:pb-32 sm:pt-36 lg:px-12">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-12">
          {/* Text + Kontaktdaten */}
          <div
            className={`transition-[opacity,transform] duration-700 ease-out lg:col-span-5 ${
              inView ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            }`}
          >
            <p className="font-display text-[11px] font-light uppercase tracking-[0.32em] text-light-muted">
              Kontakt
            </p>
            <h2 className="mt-6 font-display text-3xl font-light leading-tight tracking-tight text-light-foreground sm:text-4xl lg:text-5xl">
              Kostenlosen Entwurf
              <br className="hidden sm:block" /> anfragen.
            </h2>
            <div
              aria-hidden
              className="mt-7 h-px w-24 bg-[linear-gradient(90deg,hsl(222_90%_45%),hsl(265_70%_58%/0.15))]"
            />
            <p className="mt-7 max-w-md text-sm font-light leading-relaxed text-light-muted sm:text-base">
              Erzähl uns kurz von deinem Unternehmen. Wir melden uns innerhalb
              von 24 Stunden mit Terminvorschlägen für das kostenlose
              Erstgespräch.
            </p>

            <ul className="mt-10 flex flex-col divide-y divide-light-border border-y border-light-border">
              {CONTACT_DETAILS.map(({ icon: Icon, label, caption }, index) => (
                <li
                  key={label}
                  className={`flex items-center gap-4 py-4 transition-[opacity,transform] duration-700 ease-out ${
                    inView ? "translate-x-0 opacity-100" : "-translate-x-3 opacity-0"
                  }`}
                  style={{ transitionDelay: `${200 + index * 90}ms` }}
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-light-border bg-[hsl(0_0%_100%/0.7)]">
                    <Icon
                      className="h-4 w-4 text-[hsl(222_85%_45%)]"
                      strokeWidth={1.5}
                    />
                  </span>
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate font-display text-sm font-light tracking-wide text-light-foreground">
                      {label}
                    </span>
                    <span className="mt-0.5 text-xs font-light text-light-muted">
                      {caption}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Formular */}
          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              className={`border border-light-border bg-[hsl(0_0%_100%/0.9)] p-7 shadow-[0_36px_80px_-52px_hsl(225_50%_28%/0.7)] transition-[opacity,transform] duration-[900ms] ease-out sm:p-10 ${
                inView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
              }`}
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="name" className="text-xs font-light tracking-wide text-light-muted">
                    Name *
                  </Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Vor- und Nachname"
                    className="h-12 rounded-none border-light-border bg-[hsl(0_0%_100%)] text-light-foreground placeholder:text-light-muted/60 focus-visible:ring-[hsl(222_90%_55%)]"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="company" className="text-xs font-light tracking-wide text-light-muted">
                    Unternehmen
                  </Label>
                  <Input
                    id="company"
                    value={form.company}
                    onChange={update("company")}
                    placeholder="Firmenname"
                    className="h-12 rounded-none border-light-border bg-[hsl(0_0%_100%)] text-light-foreground placeholder:text-light-muted/60 focus-visible:ring-[hsl(222_90%_55%)]"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="email" className="text-xs font-light tracking-wide text-light-muted">
                    E-Mail *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="name@unternehmen.de"
                    className="h-12 rounded-none border-light-border bg-[hsl(0_0%_100%)] text-light-foreground placeholder:text-light-muted/60 focus-visible:ring-[hsl(222_90%_55%)]"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="phone" className="text-xs font-light tracking-wide text-light-muted">
                    Telefon
                  </Label>
                  <Input
                    id="phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="Optional"
                    className="h-12 rounded-none border-light-border bg-[hsl(0_0%_100%)] text-light-foreground placeholder:text-light-muted/60 focus-visible:ring-[hsl(222_90%_55%)]"
                  />
                </div>

                <div className="flex flex-col gap-2 sm:col-span-2">
                  <Label className="text-xs font-light tracking-wide text-light-muted">
                    Interessantes Paket
                  </Label>
                  <Select
                    value={form.plan}
                    onValueChange={(value) =>
                      setForm((prev) => ({ ...prev, plan: value }))
                    }
                  >
                    <SelectTrigger className="h-12 rounded-none border-light-border bg-[hsl(0_0%_100%)] text-light-foreground focus:ring-[hsl(222_90%_55%)]">
                      <SelectValue placeholder="Bitte auswählen" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="website-abo">Website-Abo · 59 €</SelectItem>
                      <SelectItem value="business">Business · 79 €</SelectItem>
                      <SelectItem value="premium">Premium Enterprise · 129 €</SelectItem>
                      <SelectItem value="beratung">Noch unsicher – bitte beraten</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-2 sm:col-span-2">
                  <Label htmlFor="message" className="text-xs font-light tracking-wide text-light-muted">
                    Worum geht es?
                  </Label>
                  <Textarea
                    id="message"
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Kurz zu deinem Unternehmen, deinen Zielen und einem Wunschtermin."
                    className="resize-none rounded-none border-light-border bg-[hsl(0_0%_100%)] text-light-foreground placeholder:text-light-muted/60 focus-visible:ring-[hsl(222_90%_55%)]"
                  />
                </div>
              </div>

              <div className="mt-7 flex items-start gap-3">
                <Checkbox
                  id="privacy"
                  checked={form.privacy}
                  onCheckedChange={(value) =>
                    setForm((prev) => ({ ...prev, privacy: Boolean(value) }))
                  }
                  className="mt-0.5 rounded-none border-light-border data-[state=checked]:border-[hsl(222_85%_48%)] data-[state=checked]:bg-[hsl(222_85%_48%)]"
                />
                <Label
                  htmlFor="privacy"
                  className="text-xs font-light leading-relaxed text-light-muted"
                >
                  Ich bin damit einverstanden, dass meine Angaben zur Bearbeitung
                  der Anfrage gespeichert werden. Weitere Infos in der{" "}
                  <a
                    href="/datenschutz"
                    className="underline decoration-[hsl(222_85%_48%)] underline-offset-2 transition-colors hover:text-light-foreground"
                  >
                    Datenschutzerklärung
                  </a>
                  . *
                </Label>
              </div>

              <div className="mt-8 flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs font-light text-light-muted">
                  * Pflichtfelder · Wir antworten meist am selben Tag
                </p>

                <button
                  type="submit"
                  disabled={sending}
                  className="group flex items-center gap-2 outline-none disabled:opacity-70"
                >
                  <span
                    className="flex items-center gap-2 bg-gradient-cta px-8 py-3.5 font-display text-sm font-normal tracking-wide text-primary-foreground transition-transform duration-300 group-hover:-translate-y-0.5"
                    style={{ transform: "skewX(-12deg)" }}
                  >
                    <span
                      className="flex items-center gap-2"
                      style={{ transform: "skewX(12deg)" }}
                    >
                      {sending ? "Wird gesendet …" : "Anfrage senden"}
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
